import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { SpeciesDetailModal } from '@/components/ui/SpeciesDetailModal'
import { router } from '@inertiajs/react'
import { PLACEHOLDER_ICONS } from '@/lib/icons'
import { useState, useEffect } from 'react'

interface PlantSpecies {
  id: number
  latin_name: string
  common_name_pl: string
  family: string | null
  default_watering_frequency_days: number | null
  default_watering_amount_ml: number | null
  sunlight_preference: string | null
  sunlight_preference_pl: string | null
  difficulty_level: string | null
  difficulty_level_pl: string | null
  temperature_min: number | null
  temperature_max: number | null
  humidity_preference: string | null
  care_tips: string[]
  common_problems: string[]
  source: string | null
  illustration_url: string | null
  thumbnail_url: string | null
}

interface Props {
  plant_species: PlantSpecies[]
  filters: { q?: string; sort?: string }
}

const sortOptions = [
  { value: '', label: 'A-Z' },
  { value: 'difficulty', label: 'Trudność' },
  { value: 'newest', label: 'Najnowsze' },
]

function PlantSpeciesIndex({ plant_species, filters }: Props) {
  const [query, setQuery] = useState(filters.q || '')
  const [selected, setSelected] = useState<PlantSpecies | null>(null)

  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get('/plant_species', { q: query || undefined, sort: filters.sort || undefined }, { preserveState: true, replace: true })
    }, 300)
    return () => clearTimeout(timeout)
  }, [query])

  const handleSort = (sort: string) => {
    router.get('/plant_species', { q: filters.q || undefined, sort: sort || undefined }, { preserveState: true, replace: true })
  }

  return (
    <AtlasPage>
      <PageTitle title="Baza gatunków" subtitle="Baza wiedzy o gatunkach roślin" />
      <Separator />

      {/* Search */}
      <div className="mb-md">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Szukaj gatunku..."
          className="w-full border border-frame bg-paper-dark px-md py-sm font-body text-sm text-ink placeholder-frame/60 focus:border-botanical focus:outline-none"
        />
      </div>

      {/* Sort buttons */}
      <div className="flex flex-wrap items-center justify-center gap-xs mb-md">
        {sortOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => handleSort(opt.value)}
            className={`px-sm py-xs font-label text-xs tracking-wide border transition-colors ${
              (filters.sort || '') === opt.value
                ? 'border-botanical bg-botanical text-white'
                : 'border-frame text-frame hover:bg-paper-dark'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-xs text-frame/60 font-label ml-sm">
          {plant_species.length} gatunków
        </span>
      </div>

      {/* Grid */}
      {plant_species.length > 0 ? (
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3">
          {plant_species.map((species) => (
            <button
              key={species.id}
              onClick={() => setSelected(species)}
              className="group block border border-frame/30 bg-paper-dark/50 p-sm transition-shadow hover:shadow-hover text-left cursor-pointer"
            >
              {species.thumbnail_url ? (
                <div className="aspect-square w-full overflow-hidden mb-sm" style={{ border: '1px solid var(--desk)' }}>
                  <img src={species.thumbnail_url} alt={species.common_name_pl} className="h-full w-full object-contain" loading="lazy" />
                </div>
              ) : (
                <div className="aspect-square w-full flex items-center justify-center mb-sm" style={{ background: 'var(--desk)', opacity: 0.5 }}>
                  <PLACEHOLDER_ICONS.species.icon weight="regular" size={48} className="text-frame/40" />
                </div>
              )}
              <h3 className="font-display text-sm uppercase tracking-wider text-ink group-hover:text-botanical">
                {species.common_name_pl}
              </h3>
              <p className="text-xs italic text-frame mt-xs">{species.latin_name}</p>
              <div className="flex flex-wrap gap-xs mt-sm">
                {species.difficulty_level_pl && (
                  <span className="px-[6px] py-[2px] text-[0.65rem] font-label bg-paper-dark text-ink-light leading-none">
                    {species.difficulty_level_pl}
                  </span>
                )}
                {species.sunlight_preference_pl && (
                  <span className="px-[6px] py-[2px] text-[0.65rem] font-label bg-paper-dark text-ink-light leading-none">
                    {species.sunlight_preference_pl}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-xl">
          <p className="text-frame italic font-label">Nie znaleziono gatunków.</p>
          <p className="text-frame/60 text-xs mt-xs">Spróbuj innej frazy lub poproś Andrzeja o dodanie nowego gatunku.</p>
        </div>
      )}

      <Separator />
      <PageNumber number={6} />

      <SpeciesDetailModal species={selected} onClose={() => setSelected(null)} />
    </AtlasPage>
  )
}

PlantSpeciesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default PlantSpeciesIndex
